import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';

interface SyncTableProps<TData = any> extends Partial<TableOptions<TData>> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  maxH?: string | number;
  /** Min table width so narrow viewports scroll horizontally instead of clipping columns. */
  minTableW?: string | number;
}

/**
 * DS-styled table for Synchronization screens — mockup `.table` spec
 * (design-guidelines.md §4 Table): r-lg border surface wrapper, sticky
 * uppercase xs/600 subtle thead on bg.canvas, td border-bottom border.default,
 * row hover bg.canvas. UI-only; wiring (columns/data) passed through verbatim.
 */
const SyncTable = <TData,>({
  columns,
  data,
  enableRowSelection,
  maxH = '600px',
  minTableW = '720px',
  ...rest
}: SyncTableProps<TData>) => {
  const table = useReactTable<TData>({
    data,
    columns,
    state: {},
    defaultColumn: { size: -1, minSize: -1 },
    enableRowSelection: enableRowSelection || false,
    getCoreRowModel: getCoreRowModel(),
    ...rest,
  });

  return (
    <TableContainer
      sx={{ WebkitOverflowScrolling: 'touch', boxSizing: 'border-box' }}
      pos='relative'
      overflowX='auto'
      overflowY='auto'
      maxH={maxH}
      bg='bg.surface'
      borderWidth='1px'
      borderColor='border.default'
      // Always allow horizontal scroll affordance; -webkit for momentum scroll on iOS.
      borderRadius='lg'
    >
      <Table w='100%' minW={minTableW}>
        <Thead pos='sticky' zIndex={2} top={0}>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  sx={{
                    width: header.getSize() === -1 ? 'auto' : `${header.getSize()}px`,
                    minW: (header.column.columnDef.meta as any)?.minW ?? '120px',
                    whiteSpace: 'nowrap',
                    bg: 'bg.canvas',
                    color: 'text.subtle',
                    textTransform: 'uppercase',
                    fontSize: 'xs',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    borderBottomWidth: '1px',
                    borderColor: 'border.default',
                    py: 3,
                    px: 4,
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr
              key={row.id}
              _hover={{ bg: 'bg.canvas' }}
              transition='background-color 0.12s ease-out'
            >
              {row.getVisibleCells().map((cell) => {
                // Columns can opt out of truncation (e.g. the Site URL must show in full). (#78323)
                // and override the default min/max width (e.g. wide Folders, narrow row actions).
                const { noTruncate, minW, maxW } = (cell.column.columnDef.meta as any) || {};
                return (
                  <Td
                    key={cell.id}
                    sx={{
                      whiteSpace: noTruncate ? 'normal' : 'nowrap',
                      wordBreak: noTruncate ? 'break-word' : 'normal',
                      minW: minW ?? '120px',
                      maxW: maxW ?? (noTruncate ? '460px' : '320px'),
                      overflow: noTruncate ? 'visible' : 'hidden',
                      textOverflow: noTruncate ? 'clip' : 'ellipsis',
                      color: 'text.default',
                      fontSize: 'sm',
                      borderBottomWidth: '1px',
                      borderColor: 'border.default',
                      py: 3,
                      px: 4,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default SyncTable;
